/**
 * Shared utility to refresh Postgres-backed facet data for the medical PA worklist:
 * tab counts, org-wide unique filter option counts, and review-pending sidebar facets.
 * @module refreshPostgresFacetCounts
 */

import { getCurrentUserId } from "@risalabs_frontend_org/auth-wrapper";
import { getAllMedicalPaFilterKeysToFetchFromPostgres } from "../enums/medicalPAFilterKeys";
import { MedicalOrderReviewStatus } from "../enums/orderReviewStatus";
import { AlgoliaFacetAttribute } from "../enums/worklistEnums";
import {
  fetchCountsByMasterAuthStatusByPostgres,
  fetchPostgresFacetResults,
  getUniqueFilterOptions,
} from "../redux/slice/medicalPa/postgresFacetsCountsSlice";
import { AppDispatch } from "../redux/store/store";
import { getOrgIdForFetchExternalWorklist } from "./organizationHelper";

/**
 * Dispatches three Postgres facet-related thunks for the current org and user:
 *
 * 1. `fetchCountsByMasterAuthStatusByPostgres` — tab-level counts by master auth status
 *    (scoped to the signed-in user via `assigned_to`).
 * 2. `getUniqueFilterOptions` — all configured medical-PA facet keys without assignee
 *    or created-at filters; results live in `postgresFacetCounts.unique_filter_options`.
 * 3. `fetchPostgresFacetResults` — review-pending master-auth-status counts for the sidebar.
 *
 * Call after mutations or on mount whenever tab, filter-option, or review-pending counts may be stale.
 *
 * @param dispatch - Redux dispatch function (typed as AppDispatch).
 */
export const refreshPostgresFacetCounts = (dispatch: AppDispatch) => {
  const orgId = getOrgIdForFetchExternalWorklist();
  const assignedTo = getCurrentUserId() ?? "";

  dispatch(
    fetchCountsByMasterAuthStatusByPostgres({
      org_id: orgId,
      assigned_to: assignedTo,
      facet_attributes: getAllMedicalPaFilterKeysToFetchFromPostgres(),
    }),
  );

  dispatch(
    getUniqueFilterOptions({
      org_id: orgId,
      facet_attributes: getAllMedicalPaFilterKeysToFetchFromPostgres(),
    }),
  );

  dispatch(
    fetchPostgresFacetResults({
      org_id: orgId,
      assigned_to: assignedTo,
      facet_attributes: [AlgoliaFacetAttribute.MasterAuthStatus],
      facet_filters: {
        [AlgoliaFacetAttribute.MedicalOrderReviewStatus]: [
          MedicalOrderReviewStatus.AGENT_ORDER_REVIEW_PENDING,
        ],
      },
    }),
  );
};
